/**
 * The AI assistant's data layer.
 *
 * One query per brand for the settings payload — the style guides, the credit state and whether
 * the installation can write at all travel together because the panel and the composer both need
 * all three, and two calls would let them disagree.
 *
 * Every mutation invalidates that query rather than patching the cache. Saving one network's
 * instructions changes `updatedAt`, and a generation changes the credit counter — both are
 * server-derived, and a locally patched copy would be right about the text and wrong about the
 * number beside it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { AiCreditBalance, AiCreditHistory, Provider } from '@ftk-connector/shared'

import {
  buyAiCredits,
  cancelSchedulingJob,
  confirmSchedulingJob,
  createSchedulingJob,
  deleteChatSession,
  fetchAiCreditBalance,
  fetchAiCreditHistory,
  fetchAiSettings,
  fetchChatSession,
  fetchChatSessions,
  fetchSchedulingJob,
  fetchSchedulingJobGroups,
  fetchSchedulingJobMembership,
  fetchSchedulingJobPosts,
  previewSchedulingJob,
  renameSchedulingJob,
  requestAssist,
  resetAiStyle,
  saveAiStyle,
  saveAsDraftsSchedulingJob,
  sendChatMessage,
  startChat,
} from './api'
import type {
  AiStyle,
  AiStyleSettings,
  AssistInput,
  AssistResult,
  ChatSession,
  ChatSessionSummary,
  ConfirmedPostInput,
  DraftedPostInput,
  GroupPost,
  SchedulingJob,
  SchedulingJobGroup,
  SchedulingJobInput,
  SchedulingJobMembership,
  SchedulingJobPreview,
} from './types'

export const aiSettingsKey = (brandId: string): readonly unknown[] => ['pd', 'ai', brandId]

export const useAiSettings = (brandId: string | null): UseQueryResult<AiStyleSettings> =>
  useQuery({
    queryKey: aiSettingsKey(brandId ?? 'none'),
    enabled: brandId !== null,
    staleTime: 30_000,
    queryFn: () => {
      if (brandId === null) throw new Error('There is no brand to read AI settings for.')
      return fetchAiSettings(brandId)
    },
  })

export interface SaveStyleVariables {
  brandId: string
  provider: Provider
  instructions: string
}

export const useSaveAiStyle = (): UseMutationResult<AiStyle, Error, SaveStyleVariables> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ brandId, provider, instructions }: SaveStyleVariables) =>
      saveAiStyle(brandId, provider, instructions),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: aiSettingsKey(variables.brandId) })
    },
  })
}

export interface ResetStyleVariables {
  brandId: string
  provider: Provider
}

export const useResetAiStyle = (): UseMutationResult<AiStyle, Error, ResetStyleVariables> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ brandId, provider }: ResetStyleVariables) => resetAiStyle(brandId, provider),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: aiSettingsKey(variables.brandId) })
    },
  })
}

export interface AssistVariables extends AssistInput {
  brandId: string
}

/**
 * One generation.
 *
 * `retry: false`, and deliberately: every attempt is a paid call, a refusal is a real answer
 * rather than a blip, and a silent retry would spend a second credit on a request the author
 * never made twice.
 */
export const useAiAssist = (): UseMutationResult<AssistResult, Error, AssistVariables> => {
  const queryClient = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: ({ brandId, ...input }: AssistVariables) => requestAssist(brandId, input),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: aiSettingsKey(variables.brandId) })
    },
  })
}

export const aiCreditBalanceKey = (brandId: string): readonly unknown[] => [
  'pd',
  'ai',
  'credits',
  brandId,
]

/** The nav rail's own read: balance, reset date and the price/quantity bounds the buy dialog needs. */
export const useAiCreditBalance = (brandId: string | null): UseQueryResult<AiCreditBalance> =>
  useQuery({
    queryKey: aiCreditBalanceKey(brandId ?? 'none'),
    enabled: brandId !== null,
    staleTime: 30_000,
    queryFn: () => {
      if (brandId === null) throw new Error('There is no brand to read AI credits for.')
      return fetchAiCreditBalance(brandId)
    },
  })

export const aiCreditHistoryKey = (brandId: string): readonly unknown[] => [
  'pd',
  'ai',
  'credits',
  'history',
  brandId,
]

/** The dedicated credits page's own read: every purchase, and a year of monthly usage. */
export const useAiCreditHistory = (brandId: string | null): UseQueryResult<AiCreditHistory> =>
  useQuery({
    queryKey: aiCreditHistoryKey(brandId ?? 'none'),
    enabled: brandId !== null,
    staleTime: 30_000,
    queryFn: () => {
      if (brandId === null) throw new Error('There is no brand to read AI credit history for.')
      return fetchAiCreditHistory(brandId)
    },
  })

export interface BuyAiCreditsVariables {
  brandId: string
  credits: number
}

/**
 * Start a purchase. `retry: false`, as with every call here that reaches Stripe: a refusal is an
 * answer, and a silent second attempt would open a second checkout session for the same intent.
 *
 * Returns the Stripe url only — the balance is credited by the webhook, not the redirect, so the
 * caller sends the browser there and lets `useAiCreditBalance` refetch when it comes back.
 */
export const useBuyAiCredits = (): UseMutationResult<{ url: string }, Error, BuyAiCreditsVariables> =>
  useMutation({
    retry: false,
    mutationFn: ({ brandId, credits }: BuyAiCreditsVariables) => buyAiCredits(brandId, credits),
  })

/* ── the batch scheduler ───────────────────────────────────────────────────────────────────
   Three mutations and one query, and the invalidations are what separate them:

   - **Drafting** spends credits, so it refreshes the AI settings and nothing else — it has not
     touched a calendar and invalidating the planner would be claiming it had.
   - **Confirming** creates posts, so it invalidates the planner as well. This is the single place
     in this feature where that is true.
   - **Cancelling** touches neither. */

/** The planner's root key, mirroring `features/planner/queries.ts`. A confirm moves its data. */
const PLANNER_ROOT = ['pd', 'planner'] as const

/** The "Gruppi" tab's own list. Confirm, save-as-drafts and rename all move it. */
export const schedulingJobGroupsKey = (brandId: string): readonly unknown[] => [
  'pd',
  'ai',
  'scheduling',
  'groups',
  brandId,
]

export const schedulingJobKey = (brandId: string, jobId: string): readonly unknown[] => [
  'pd',
  'ai',
  'scheduling',
  brandId,
  jobId,
]

/**
 * Poll one job.
 *
 * Enabled only while the job is pre-terminal, and off entirely for the ordinary path where the
 * draft came back on the request that started it: polling a finished job would be a request per
 * interval asking a question already answered.
 */
export const useSchedulingJob = (
  brandId: string | null,
  jobId: string | null,
  options: { poll?: boolean } = {},
): UseQueryResult<SchedulingJob> =>
  useQuery({
    queryKey: schedulingJobKey(brandId ?? 'none', jobId ?? 'none'),
    enabled: brandId !== null && jobId !== null,
    refetchInterval: options.poll === true ? 2_000 : false,
    queryFn: () => {
      if (brandId === null || jobId === null) throw new Error('There is no job to read.')
      return fetchSchedulingJob(brandId, jobId)
    },
  })

/**
 * What drafting this brief would do — free, no model call, nothing written.
 *
 * A mutation rather than a query: it runs once, when the author moves past the brief, not on
 * every keystroke — the confirm screen's own trigger, same shape as every other paid-adjacent
 * call in this feature.
 */
export const useSchedulingJobPreview = (): UseMutationResult<
  SchedulingJobPreview,
  Error,
  CreateSchedulingJobVariables
> =>
  useMutation({
    retry: false,
    mutationFn: ({ brandId, ...input }: CreateSchedulingJobVariables) =>
      previewSchedulingJob(brandId, input),
  })

export interface CreateSchedulingJobVariables extends SchedulingJobInput {
  brandId: string
}

/**
 * Draft a batch. **Schedules nothing.**
 *
 * `retry: false`, for the same reason the assist does not retry: every attempt is a paid call,
 * and a silent second one would spend a second set of credits on a request nobody made twice.
 */
export const useCreateSchedulingJob = (): UseMutationResult<
  SchedulingJob,
  Error,
  CreateSchedulingJobVariables
> => {
  const queryClient = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: ({ brandId, ...input }: CreateSchedulingJobVariables) =>
      createSchedulingJob(brandId, input),
    onSuccess: (job, variables) => {
      queryClient.setQueryData(schedulingJobKey(variables.brandId, job.id), job)
      void queryClient.invalidateQueries({ queryKey: aiSettingsKey(variables.brandId) })
    },
  })
}

export interface ConfirmSchedulingVariables {
  brandId: string
  jobId: string
  /** The batch as the author left it. Omitted schedules the proposal unchanged. */
  posts?: readonly ConfirmedPostInput[]
}

/** Schedule the reviewed batch. The one mutation here that puts anything in the planner. */
export const useConfirmSchedulingJob = (): UseMutationResult<
  SchedulingJob,
  Error,
  ConfirmSchedulingVariables
> => {
  const queryClient = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: ({ brandId, jobId, posts }: ConfirmSchedulingVariables) =>
      confirmSchedulingJob(brandId, jobId, posts),
    onSuccess: (job, variables) => {
      queryClient.setQueryData(schedulingJobKey(variables.brandId, job.id), job)
      void queryClient.invalidateQueries({ queryKey: PLANNER_ROOT })
      void queryClient.invalidateQueries({ queryKey: schedulingJobGroupsKey(variables.brandId) })
    },
  })
}

export interface CancelSchedulingVariables {
  brandId: string
  jobId: string
}

export const useCancelSchedulingJob = (): UseMutationResult<
  SchedulingJob,
  Error,
  CancelSchedulingVariables
> => {
  const queryClient = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: ({ brandId, jobId }: CancelSchedulingVariables) =>
      cancelSchedulingJob(brandId, jobId),
    onSuccess: (job, variables) => {
      queryClient.setQueryData(schedulingJobKey(variables.brandId, job.id), job)
    },
  })
}

export interface SaveAsDraftsVariables {
  brandId: string
  jobId: string
  /** The batch as the author left it. Omitted keeps every proposal still in the review list. */
  posts?: readonly DraftedPostInput[]
}

/**
 * Keep the batch without arming it. **This also puts posts in the planner** — drafts, not
 * scheduled ones — so it invalidates the same query `useConfirmSchedulingJob` does: a group's
 * drafts exist the moment this lands, same as a confirm's scheduled posts do.
 */
export const useSaveAsDraftsSchedulingJob = (): UseMutationResult<
  SchedulingJob,
  Error,
  SaveAsDraftsVariables
> => {
  const queryClient = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: ({ brandId, jobId, posts }: SaveAsDraftsVariables) =>
      saveAsDraftsSchedulingJob(brandId, jobId, posts),
    onSuccess: (job, variables) => {
      queryClient.setQueryData(schedulingJobKey(variables.brandId, job.id), job)
      void queryClient.invalidateQueries({ queryKey: PLANNER_ROOT })
      void queryClient.invalidateQueries({ queryKey: schedulingJobGroupsKey(variables.brandId) })
    },
  })
}

export interface RenameSchedulingJobVariables {
  brandId: string
  jobId: string
  name: string
}

export const useRenameSchedulingJob = (): UseMutationResult<
  SchedulingJob,
  Error,
  RenameSchedulingJobVariables
> => {
  const queryClient = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: ({ brandId, jobId, name }: RenameSchedulingJobVariables) =>
      renameSchedulingJob(brandId, jobId, name),
    onSuccess: (job, variables) => {
      queryClient.setQueryData(schedulingJobKey(variables.brandId, job.id), job)
      void queryClient.invalidateQueries({ queryKey: schedulingJobGroupsKey(variables.brandId) })
    },
  })
}

/** The "Gruppi" tab's own list — one card per group, most recent first. */
export const useSchedulingJobGroups = (brandId: string | null): UseQueryResult<SchedulingJobGroup[]> =>
  useQuery({
    queryKey: schedulingJobGroupsKey(brandId ?? 'none'),
    enabled: brandId !== null,
    queryFn: () => {
      if (brandId === null) throw new Error('There is no brand to list groups for.')
      return fetchSchedulingJobGroups(brandId)
    },
  })

export const schedulingJobPostsKey = (brandId: string, jobId: string): readonly unknown[] => [
  'pd',
  'ai',
  'scheduling',
  'group-posts',
  brandId,
  jobId,
]

/** One group's own posts, for its detail view. */
export const useSchedulingJobPosts = (
  brandId: string | null,
  jobId: string | null,
): UseQueryResult<GroupPost[]> =>
  useQuery({
    queryKey: schedulingJobPostsKey(brandId ?? 'none', jobId ?? 'none'),
    enabled: brandId !== null && jobId !== null,
    queryFn: () => {
      if (brandId === null || jobId === null) throw new Error('There is no group to read.')
      return fetchSchedulingJobPosts(brandId, jobId)
    },
  })

/**
 * Which of these calendar post ids belong to a group — `PostCard`'s badge.
 *
 * Keyed on the id list itself (sorted, so a reorder of the same window is not a different
 * query) rather than on the window's dates: two different months that happen to show the same
 * posts should share one cache entry, not refetch for each.
 */
export const useSchedulingJobMembership = (
  brandId: string | null,
  postIds: readonly number[],
): UseQueryResult<SchedulingJobMembership> => {
  const sorted = [...postIds].sort((a, b) => a - b)
  return useQuery({
    queryKey: ['pd', 'ai', 'scheduling', 'membership', brandId ?? 'none', sorted.join(',')],
    enabled: brandId !== null,
    staleTime: 30_000,
    queryFn: () => {
      if (brandId === null) throw new Error('There is no brand to check group membership for.')
      return fetchSchedulingJobMembership(brandId, sorted)
    },
  })
}

/* ── the conversational assistant ──────────────────────────────────────────────────────────
   Two queries and three mutations. The invalidations say what each one actually changed:

   - **A turn** spends a credit and appends to one transcript, so it writes that transcript into
     the cache and refreshes the AI settings (the credit counter) and the session list (the title
     and the "most recently used" order). It invalidates **nothing else** — in particular not the
     planner, because nothing said in a conversation puts anything on a calendar.
   - **Deleting** removes a transcript and touches no counter: credits already spent bought
     answers that were given. */

/* The two keys are siblings rather than parent and child on purpose: invalidating the list must
   not also invalidate every transcript under it, which would refetch the answer this turn just
   wrote into the cache. */
export const chatSessionsKey = (brandId: string): readonly unknown[] => [
  'pd',
  'ai',
  'chat',
  'list',
  brandId,
]

export const chatSessionKey = (brandId: string, sessionId: string): readonly unknown[] => [
  'pd',
  'ai',
  'chat',
  'session',
  brandId,
  sessionId,
]

export const useChatSessions = (brandId: string | null): UseQueryResult<ChatSessionSummary[]> =>
  useQuery({
    queryKey: chatSessionsKey(brandId ?? 'none'),
    enabled: brandId !== null,
    staleTime: 30_000,
    queryFn: () => {
      if (brandId === null) throw new Error('There is no brand to list conversations for.')
      return fetchChatSessions(brandId)
    },
  })

export const useChatSession = (
  brandId: string | null,
  sessionId: string | null,
): UseQueryResult<ChatSession> =>
  useQuery({
    queryKey: chatSessionKey(brandId ?? 'none', sessionId ?? 'none'),
    enabled: brandId !== null && sessionId !== null,
    queryFn: () => {
      if (brandId === null || sessionId === null) {
        throw new Error('There is no conversation to read.')
      }
      return fetchChatSession(brandId, sessionId)
    },
  })

export interface StartChatVariables {
  brandId: string
  message: string
}

/**
 * Open a conversation.
 *
 * `retry: false`, as with every paid call in this file: a refusal is an answer, and a silent
 * second attempt would spend a second credit on a question nobody asked twice.
 */
export const useStartChat = (): UseMutationResult<ChatSession, Error, StartChatVariables> => {
  const queryClient = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: ({ brandId, message }: StartChatVariables) => startChat(brandId, message),
    onSuccess: (session, variables) => {
      queryClient.setQueryData(chatSessionKey(variables.brandId, session.id), session)
      void queryClient.invalidateQueries({ queryKey: chatSessionsKey(variables.brandId) })
      void queryClient.invalidateQueries({ queryKey: aiSettingsKey(variables.brandId) })
    },
  })
}

export interface SendChatMessageVariables extends StartChatVariables {
  sessionId: string
}

export const useSendChatMessage = (): UseMutationResult<
  ChatSession,
  Error,
  SendChatMessageVariables
> => {
  const queryClient = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: ({ brandId, sessionId, message }: SendChatMessageVariables) =>
      sendChatMessage(brandId, sessionId, message),
    onSuccess: (session, variables) => {
      queryClient.setQueryData(chatSessionKey(variables.brandId, session.id), session)
      void queryClient.invalidateQueries({ queryKey: chatSessionsKey(variables.brandId) })
      void queryClient.invalidateQueries({ queryKey: aiSettingsKey(variables.brandId) })
    },
  })
}

export interface DeleteChatVariables {
  brandId: string
  sessionId: string
}

export const useDeleteChatSession = (): UseMutationResult<void, Error, DeleteChatVariables> => {
  const queryClient = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: ({ brandId, sessionId }: DeleteChatVariables) =>
      deleteChatSession(brandId, sessionId),
    onSuccess: (_data, variables) => {
      queryClient.removeQueries({ queryKey: chatSessionKey(variables.brandId, variables.sessionId) })
      void queryClient.invalidateQueries({ queryKey: chatSessionsKey(variables.brandId) })
    },
  })
}
